import React, { forwardRef, useMemo, useRef } from 'react';
import { useLocale } from 'react-aria';
import { useNumberFieldState } from 'react-stately';
import { useNumberField } from 'react-aria';
import BaseInput, { InputProps } from '../_base/Input/Input';
import { altoConsole } from '../../utils/console/console';

type NumberFieldHookProps = Parameters<typeof useNumberFieldState>[0];

type NumberFieldComponent = React.ForwardRefExoticComponent<
  NumberFieldProps & React.RefAttributes<HTMLInputElement>
> & {
  StartSlot: typeof BaseInput.StartSlot;
  EndSlot: typeof BaseInput.EndSlot;
  Prefix: typeof BaseInput.Prefix;
  Suffix: typeof BaseInput.Suffix;
  StartSelect: typeof BaseInput.StartSelect;
  EndSelect: typeof BaseInput.EndSelect;
  StartIcon: typeof BaseInput.StartIcon;
  EndIcon: typeof BaseInput.EndIcon;
};

export interface NumberFieldProps
  extends Omit<
    InputProps,
    'maxWidthByChars' | 'value' | 'defaultValue' | 'onChange'
  > {
  /**
   * Number formatting options (Intl.NumberFormat)
   */
  formatOptions?: Intl.NumberFormatOptions;

  /**
   * Step value for increment/decrement
   */
  step?: number;
  /**
   * Text alignment for the input field
   * @default "center"
   */
  textAlign?: 'left' | 'center' | 'right';
  /**
   * The minimum value for the NumberField
   *
   */
  minValue?: number;
  /**
   * The maximum value for the NumberField
   */
  maxValue?: number;
  /**
   * Test id for the component
   */
  'data-testid'?: string;
  /**
   * Starting value of the number field (uncontrolled)
   */
  defaultValue?: string | number;
  /**
   * Value of the number field (controlled)
   */
  value?: string | number;
  /**
   * Function called when the numberfield changes value. `onChange` will be called with the same type as `defaultValue` or `value` i.e. number or string
   */
  onChange?: ((newValue: string) => void) | ((newValue: number) => void);
}

function getStateValue(val: string | number | undefined) {
  if (typeof val === 'string') {
    if (val === '' || isNaN(Number(val))) return undefined;
    return Number(val);
  }
  return val;
}

function mergeAriaIds(
  hookValue: string | undefined,
  explicitValue: string | undefined,
) {
  const merged = [hookValue, explicitValue]
    .filter(Boolean)
    .flatMap((value) => value!.split(' '))
    .map((value) => value.trim())
    .filter(Boolean);

  if (merged.length === 0) return undefined;
  return Array.from(new Set(merged)).join(' ');
}

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  (
    {
      minValue,
      maxValue,
      value: controlledValue,
      onChange,
      defaultValue,
      textAlign = 'center',
      formatOptions,
      step,
      children,
      visuallyHiddenLabel,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    if (!props['label'] && !props['aria-label'] && !props['aria-labelledby']) {
      altoConsole.warn(
        '`label` prop is expected for accessibility compliance',
        { scope: 'NumberField' },
      );
    }

    const inputRef = useRef<HTMLInputElement | null>(null);
    const { locale } = useLocale();
    // Generate a consistent id for the input and controls
    const reactId = React.useId();
    const inputId = props.id || `numberfield-input-${reactId}`;

    const valueType = useMemo(() => {
      // Preserve string type as default
      if (controlledValue === undefined && defaultValue === undefined)
        return 'string';
      // Otherwise get the type of the value that has been passed in
      if (controlledValue) return typeof controlledValue;
      return typeof defaultValue;
    }, [controlledValue, defaultValue]);

    const {
      validate: _validate,
      label: _label,
      description: _description,
      errorMessage: _errorMessage,
      ...numberFieldBaseProps
    } = props;

    // Reuse shared field props so react-aria can wire aria relationships consistently.
    const numberFieldHookProps = {
      ...numberFieldBaseProps,
      minValue,
      maxValue,
      label: props.label, // Pass label to hook to avoid React Aria missing label warning
      value: getStateValue(controlledValue),
      defaultValue: getStateValue(defaultValue),
      onChange: onChange
        ? (val: number) => {
            return valueType === 'string'
              ? (onChange as (newValue: string) => void)(val.toString())
              : (onChange as (newValue: number) => void)(val);
          }
        : undefined,
      locale,
      formatOptions,
      step,
    } as NumberFieldHookProps;
    const state = useNumberFieldState(numberFieldHookProps);
    const { inputProps, incrementButtonProps, decrementButtonProps } =
      useNumberField(numberFieldHookProps, state, inputRef);

    const mergedRef = (element: HTMLInputElement | null) => {
      inputRef.current = element;

      if (typeof ref === 'function') {
        ref(element);
        return;
      }

      if (ref) {
        ref.current = element;
      }
    };

    const hasExplicitAriaLabelledBy = Object.prototype.hasOwnProperty.call(
      props,
      'aria-labelledby',
    );
    const hasExplicitAriaDescribedBy = Object.prototype.hasOwnProperty.call(
      props,
      'aria-describedby',
    );

    // Patch aria-controls on increment/decrement buttons to match inputId
    const patchedIncrementButtonProps = {
      ...incrementButtonProps,
      'aria-controls': inputId,
    };
    const patchedDecrementButtonProps = {
      ...decrementButtonProps,
      'aria-controls': inputId,
    };

    // Enhance slots with increment/decrement props if specified
    const enhancedChildren = React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      const newProps: {
        increment?: typeof incrementButtonProps;
        decrement?: typeof decrementButtonProps;
      } = {};
      if ('increment' in child.props) {
        newProps.increment = patchedIncrementButtonProps;
      }
      if ('decrement' in child.props) {
        newProps.decrement = patchedDecrementButtonProps;
      }
      return Object.keys(newProps).length > 0
        ? React.cloneElement(child, newProps)
        : child;
    });

    return (
      <BaseInput
        ref={mergedRef}
        {...props}
        visuallyHiddenLabel={visuallyHiddenLabel}
        inputProps={{
          ...inputProps,
          id: inputId,
          ...(hasExplicitAriaLabelledBy
            ? {
                'aria-labelledby': mergeAriaIds(
                  inputProps['aria-labelledby'],
                  props['aria-labelledby'],
                ),
              }
            : {}),
          ...(hasExplicitAriaDescribedBy
            ? {
                'aria-describedby': mergeAriaIds(
                  inputProps['aria-describedby'],
                  props['aria-describedby'],
                ),
              }
            : {}),
          style: {
            ...(inputProps.style || {}),
            textAlign,
          },
        }}
        data-testid={dataTestId}
      >
        {enhancedChildren}
      </BaseInput>
    );
  },
) as NumberFieldComponent;

NumberField.StartSlot = BaseInput.StartSlot;
NumberField.EndSlot = BaseInput.EndSlot;
NumberField.Prefix = BaseInput.Prefix;
NumberField.Suffix = BaseInput.Suffix;
NumberField.StartSelect = BaseInput.StartSelect;
NumberField.EndSelect = BaseInput.EndSelect;
NumberField.StartIcon = BaseInput.StartIcon;
NumberField.EndIcon = BaseInput.EndIcon;

// Set displayName for subcomponents
NumberField.StartSlot.displayName = 'NumberField.StartSlot';
NumberField.EndSlot.displayName = 'NumberField.EndSlot';
NumberField.Prefix.displayName = 'NumberField.Prefix';
NumberField.Suffix.displayName = 'NumberField.Suffix';
NumberField.StartSelect.displayName = 'NumberField.StartSelect';
NumberField.EndSelect.displayName = 'NumberField.EndSelect';
NumberField.StartIcon.displayName = 'NumberField.StartIcon';
NumberField.EndIcon.displayName = 'NumberField.EndIcon';

NumberField.displayName = 'NumberField';

export default NumberField;
